/**
 * UPT-2096 (P1-a, §4.7) — accesseurs de libellés des prototypes au niveau
 * module (règle i18n : jamais de clé composée dans le JSX). Le test
 * `prototypeI18n.test.ts` vérifie que chaque clé existe dans les cinq langues.
 */
import type { PrototypeDocumentRole, PrototypeWarningCode } from '../data/contracts';
import type { PrototypeUiErrorCode } from './prototypeErrors';

export type Translate = (id: string) => string;

/** Libellé d'un code d'erreur (58 codes prototype.* dont les 5 de P1-b et les 7 de L2, upload.too_large, forbidden, network, unknown). */
export const prototypeErrorLabel = (t: Translate, code: PrototypeUiErrorCode): string => {
  switch (code) {
    case 'prototype.dossier_ref': return t('moduleStudio.prototype.error.prototype.dossier_ref');
    case 'prototype.metadata_missing': return t('moduleStudio.prototype.error.prototype.metadata_missing');
    case 'prototype.metadata_invalid': return t('moduleStudio.prototype.error.prototype.metadata_invalid');
    case 'prototype.prompt_too_long': return t('moduleStudio.prototype.error.prototype.prompt_too_long');
    case 'prototype.attestation_required': return t('moduleStudio.prototype.error.prototype.attestation_required');
    case 'prototype.rights_attestation_required': return t('moduleStudio.prototype.error.prototype.rights_attestation_required');
    case 'prototype.rights_text_unknown': return t('moduleStudio.prototype.error.prototype.rights_text_unknown');
    case 'prototype.derived_unknown': return t('moduleStudio.prototype.error.prototype.derived_unknown');
    case 'prototype.derived_invalid': return t('moduleStudio.prototype.error.prototype.derived_invalid');
    case 'prototype.supersedes_invalid': return t('moduleStudio.prototype.error.prototype.supersedes_invalid');
    case 'prototype.client_warning_invalid': return t('moduleStudio.prototype.error.prototype.client_warning_invalid');
    case 'prototype.pieces': return t('moduleStudio.prototype.error.prototype.pieces');
    case 'prototype.html_encoding': return t('moduleStudio.prototype.error.prototype.html_encoding');
    case 'prototype.image_type': return t('moduleStudio.prototype.error.prototype.image_type');
    case 'prototype.image_size': return t('moduleStudio.prototype.error.prototype.image_size');
    case 'prototype.image_invalid': return t('moduleStudio.prototype.error.prototype.image_invalid');
    case 'prototype.snapshot_invalid': return t('moduleStudio.prototype.error.prototype.snapshot_invalid');
    case 'prototype.snapshot_label': return t('moduleStudio.prototype.error.prototype.snapshot_label');
    case 'prototype.snapshot_size': return t('moduleStudio.prototype.error.prototype.snapshot_size');
    case 'prototype.inventory_invalid': return t('moduleStudio.prototype.error.prototype.inventory_invalid');
    case 'prototype.inventory_bounds': return t('moduleStudio.prototype.error.prototype.inventory_bounds');
    case 'prototype.body_invalid': return t('moduleStudio.prototype.error.prototype.body_invalid');
    case 'prototype.not_found': return t('moduleStudio.prototype.error.prototype.not_found');
    case 'prototype.piece_missing': return t('moduleStudio.prototype.error.prototype.piece_missing');
    case 'prototype.prompt_absent': return t('moduleStudio.prototype.error.prototype.prompt_absent');
    case 'prototype.inventory_absent': return t('moduleStudio.prototype.error.prototype.inventory_absent');
    case 'prototype.archived': return t('moduleStudio.prototype.error.prototype.archived');
    case 'prototype.images_complete': return t('moduleStudio.prototype.error.prototype.images_complete');
    case 'prototype.no_html': return t('moduleStudio.prototype.error.prototype.no_html');
    case 'prototype.snapshot_limit': return t('moduleStudio.prototype.error.prototype.snapshot_limit');
    case 'prototype.quota': return t('moduleStudio.prototype.error.prototype.quota');
    case 'prototype.too_large': return t('moduleStudio.prototype.error.prototype.too_large');
    case 'prototype.persistence': return t('moduleStudio.prototype.error.prototype.persistence');
    case 'prototype.storage_failed': return t('moduleStudio.prototype.error.prototype.storage_failed');
    case 'prototype.storage_unavailable': return t('moduleStudio.prototype.error.prototype.storage_unavailable');
    case 'prototype.incomplete': return t('moduleStudio.prototype.error.prototype.incomplete');
    case 'prototype.warnings_pending': return t('moduleStudio.prototype.error.prototype.warnings_pending');
    case 'prototype.warnings_changed': return t('moduleStudio.prototype.error.prototype.warnings_changed');
    case 'prototype.warnings_unacknowledged': return t('moduleStudio.prototype.error.prototype.warnings_unacknowledged');
    case 'prototype.analysis_no_route': return t('moduleStudio.prototype.error.prototype.analysis_no_route');
    case 'prototype.analysis_quota': return t('moduleStudio.prototype.error.prototype.analysis_quota');
    case 'prototype.analysis_not_found': return t('moduleStudio.prototype.error.prototype.analysis_not_found');
    case 'prototype.analysis_state': return t('moduleStudio.prototype.error.prototype.analysis_state');
    case 'prototype.analysis_residency': return t('moduleStudio.prototype.error.prototype.analysis_residency');
    case 'prototype.analysis_running': return t('moduleStudio.prototype.error.prototype.analysis_running');
    case 'prototype.analysis_too_large': return t('moduleStudio.prototype.error.prototype.analysis_too_large');
    case 'prototype.budget_exceeded': return t('moduleStudio.prototype.error.prototype.budget_exceeded');
    case 'prototype.embedded_spec_absent': return t('moduleStudio.prototype.error.prototype.embedded_spec_absent');
    case 'prototype.analysis_unavailable': return t('moduleStudio.prototype.error.prototype.analysis_unavailable');
    case 'prototype.analysis_failed': return t('moduleStudio.prototype.error.prototype.analysis_failed');
    case 'prototype.analysis_invalid_output': return t('moduleStudio.prototype.error.prototype.analysis_invalid_output');
    case 'prototype.pieces_limit': return t('moduleStudio.prototype.error.prototype.pieces_limit');
    case 'prototype.markdown_too_large': return t('moduleStudio.prototype.error.prototype.markdown_too_large');
    case 'prototype.markdown_encoding': return t('moduleStudio.prototype.error.prototype.markdown_encoding');
    case 'prototype.documents_invalid': return t('moduleStudio.prototype.error.prototype.documents_invalid');
    case 'prototype.source_required': return t('moduleStudio.prototype.error.prototype.source_required');
    case 'prototype.source_invalid': return t('moduleStudio.prototype.error.prototype.source_invalid');
    case 'prototype.sources_complete': return t('moduleStudio.prototype.error.prototype.sources_complete');
    case 'upload.too_large': return t('moduleStudio.prototype.error.upload.too_large');
    case 'forbidden': return t('moduleStudio.prototype.error.forbidden');
    case 'network': return t('moduleStudio.prototype.error.network');
    default: return t('moduleStudio.prototype.error.unknown');
  }
};

/** Libellé d'un code d'avertissement. */
export const prototypeWarningLabel = (t: Translate, code: PrototypeWarningCode): string => {
  switch (code) {
    case 'pii.email': return t('moduleStudio.prototype.warning.pii.email');
    case 'pii.phone': return t('moduleStudio.prototype.warning.pii.phone');
    case 'pii.avs': return t('moduleStudio.prototype.warning.pii.avs');
    case 'pii.iban': return t('moduleStudio.prototype.warning.pii.iban');
    case 'hidden.comments': return t('moduleStudio.prototype.warning.hidden.comments');
    case 'hidden.invisible': return t('moduleStudio.prototype.warning.hidden.invisible');
    case 'hidden.zero_width': return t('moduleStudio.prototype.warning.hidden.zero_width');
    case 'external.resources': return t('moduleStudio.prototype.warning.external.resources');
    case 'external.network': return t('moduleStudio.prototype.warning.external.network');
    case 'image.unscanned': return t('moduleStudio.prototype.warning.image.unscanned');
    case 'html.encoding_fallback': return t('moduleStudio.prototype.warning.html.encoding_fallback');
    default: return t('moduleStudio.prototype.warning.image.resized');
  }
};

/** Libellé du statut d'un import. */
export const prototypeStatusLabel = (t: Translate, status: 'imported' | 'analysed' | 'archived' | 'retired'): string => {
  switch (status) {
    case 'analysed': return t('moduleStudio.prototype.status.analysed');
    case 'archived': return t('moduleStudio.prototype.status.archived');
    case 'retired': return t('moduleStudio.retirement.retired');
    default: return t('moduleStudio.prototype.status.imported');
  }
};

/** Libellé de l'intention de dérivation. */
export const prototypeDerivationLabel = (t: Translate, derivation: 'none' | 'on-request' | 'standard'): string => {
  switch (derivation) {
    case 'on-request': return t('moduleStudio.prototype.derivation.onRequest');
    case 'standard': return t('moduleStudio.prototype.derivation.standard');
    default: return t('moduleStudio.prototype.derivation.none');
  }
};

/** Libellé d'un outil déclaré à l'import. */
export const prototypeToolLabel = (t: Translate, tool: string): string => {
  switch (tool) {
    case 'claude': return t('moduleStudio.prototype.tool.claude');
    case 'chatgpt': return t('moduleStudio.prototype.tool.chatgpt');
    case 'gemini': return t('moduleStudio.prototype.tool.gemini');
    case 'v0': return t('moduleStudio.prototype.tool.v0');
    case 'lovable': return t('moduleStudio.prototype.tool.lovable');
    case 'bolt': return t('moduleStudio.prototype.tool.bolt');
    case 'figma-make': return t('moduleStudio.prototype.tool.figmaMake');
    case 'geode-studio': return t('moduleStudio.prototype.tool.geodeStudio');
    case 'handmade': return t('moduleStudio.prototype.tool.handmade');
    default: return t('moduleStudio.prototype.tool.other');
  }
};

/** Raison de blocage du bouton « Importer ». */
export const importBlockerLabel = (t: Translate, blocker: string): string => {
  switch (blocker) {
    case 'title': return t('moduleStudio.prototype.blocker.title');
    case 'files': return t('moduleStudio.prototype.blocker.files');
    case 'prompt-too-long': return t('moduleStudio.prototype.blocker.promptTooLong');
    case 'attestation-no-real-data': return t('moduleStudio.prototype.blocker.attestationNoRealData');
    case 'attestation-rights': return t('moduleStudio.prototype.blocker.attestationRights');
    case 'sources-order': return t('moduleStudio.prototype.blocker.sourcesOrder');
    case 'prompt-role': return t('moduleStudio.prototype.blocker.promptRole');
    default: return t('moduleStudio.prototype.blocker.metadataTooLarge');
  }
};

/** Raison de refus d'un fichier à la sélection. */
export const rejectionReasonLabel = (t: Translate, reason: string): string => {
  switch (reason) {
    case 'archive': return t('moduleStudio.prototype.reject.archive');
    case 'source-code': return t('moduleStudio.prototype.reject.sourceCode');
    case 'prompt-file': return t('moduleStudio.prototype.reject.promptFile');
    case 'size': return t('moduleStudio.prototype.reject.size');
    case 'empty': return t('moduleStudio.prototype.reject.empty');
    case 'count-html': return t('moduleStudio.prototype.reject.countHtmlMax');
    case 'count-images': return t('moduleStudio.prototype.reject.countImages');
    case 'count-markdown': return t('moduleStudio.prototype.reject.countMarkdown');
    case 'markdown-size': return t('moduleStudio.prototype.reject.markdownSize');
    case 'markdown-encoding': return t('moduleStudio.prototype.reject.markdownEncoding');
    case 'count-sources': return t('moduleStudio.prototype.reject.countSources');
    default: return t('moduleStudio.prototype.reject.type');
  }
};

/** Libellé du rôle d'un document Markdown. */
export const prototypeDocumentRoleLabel = (t: Translate, role: PrototypeDocumentRole): string =>
  role === 'prompt' ? t('moduleStudio.prototype.document.role.prompt') : t('moduleStudio.prototype.document.role.notes');

/** « Source {n} — {fileName} » pour une pièce html (rang 0-based). */
export const prototypeSourceLabel = (
  t: (id: string, values?: Record<string, string | number>) => string,
  piece: { fileName: string; sourceIndex: number | null }
): string => t('moduleStudio.prototype.source.label', { n: (piece.sourceIndex ?? 0) + 1, fileName: piece.fileName });

/** F0: explicit keys, shared by the tour controls and their tests. */
const TOUR_KEYS = {
  title: 'moduleStudio.prototype.tour.title',
  intro: 'moduleStudio.prototype.tour.intro',
  running: 'moduleStudio.prototype.tour.running',
  cancel: 'moduleStudio.prototype.tour.cancel',
  done: 'moduleStudio.prototype.tour.done',
  sources: 'moduleStudio.prototype.tour.sources',
  needsLogin: 'moduleStudio.prototype.tour.needsLogin',
  saveFailed: 'moduleStudio.prototype.tour.saveFailed',
  limit: 'moduleStudio.prototype.tour.limit',
  destructive: 'moduleStudio.prototype.tour.skipped.destructive',
} as const;
export const prototypeTourKey = (key: keyof typeof TOUR_KEYS): string => TOUR_KEYS[key];
